/**
 * Zod input schemas: timeTracking. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
import { ISODateSchema } from './absence.js';
// Time tracking summaries (Oct 2026)
const timeSummaryPeriodKey = z.enum(['Q1', 'Q2', 'Q3', 'Q4', 'FY']);
export const TimeClockSchema = z.object({
    date: ISODateSchema,
    action: z.enum(['check_in', 'start_break', 'end_break', 'check_out', 'adjust_check_in']),
    /** Browser UTC offset (`new Date().getTimezoneOffset()`, minutes, positive =
     *  behind UTC) so the server can later compute this user's LOCAL 19:00
     *  auto-clock-out cutoff / 17:30 recorded end without any timezone config. */
    tzOffsetMinutes: z.number().int().min(-840).max(840).optional(),
    /** `adjust_check_in` only: corrected start of the RUNNING session (epoch ms) —
     *  for "I forgot to check in at 8, it's now 10". Must be in the past, on the
     *  same day, and before any break already taken. */
    checkInAtMs: z.number().int().positive().optional(),
});
/** Manual day entry is now TIME-BASED (entry/exit + explicit breaks), stored as
 *  a real session like clock-tracked days — better for audit + consistency.
 *  Times are epoch millis (client builds them from the day's wall-clock). */
export const TimeUpsertDaySchema = z.object({
    date: ISODateSchema,
    /** Check-in instant (epoch ms). */
    checkIn: z.number().int(),
    /** Check-out instant (epoch ms); must be after checkIn. */
    checkOut: z.number().int(),
    /** Breaks within the session; each start<end and inside [checkIn,checkOut]. */
    breaks: z
        .array(z.object({ start: z.number().int(), end: z.number().int() }))
        .max(20)
        .optional()
        .default([]),
    /** User confirmed saving with < 30 min total break despite the prompt. */
    shortBreakAcknowledged: z.boolean().optional(),
    note: z.string().trim().max(500).optional(),
});
const HHMMSchema = z.string().trim().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be HH:MM');
/** Default working hours (opt-in per employee). Times are local wall-clock in `timeZone`. */
export const TimeSetDefaultHoursSchema = z
    .object({
    /** The employee (HR-controlled since 9 Oct 2026: only admins / hub admins may set default hours). */
    email: z.string().trim().email().max(200),
    enabled: z.boolean(),
    checkIn: HHMMSchema,
    checkOut: HHMMSchema,
    breakStart: HHMMSchema.nullable().optional(),
    breakEnd: HHMMSchema.nullable().optional(),
    validFrom: ISODateSchema,
    /** IANA zone from the browser (`Intl.DateTimeFormat().resolvedOptions().timeZone`). */
    timeZone: z.string().trim().min(1).max(64),
    /** Confirmed saving a default break under 30 min. */
    shortBreakAcknowledged: z.boolean().optional(),
})
    .refine((v) => (v.breakStart == null) === (v.breakEnd == null), {
    message: 'Enter both break times, or neither.',
});
const PeriodSchema = z.string().trim().regex(/^\d{4}-\d{2}$/, 'Period must be YYYY-MM');
export const TimeSubmitStatementSchema = z.object({ period: PeriodSchema });
export const TimeRecallStatementSchema = z.object({ period: PeriodSchema });
export const AdminSetTimeTrackingConfigSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    enabled: z.boolean(),
    tracksOwn: z.boolean(),
    weeklyWorkingHours: z.number().min(0).max(80).nullable(),
    timeTrackingStartDate: z
        .union([z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/), z.null()])
        .transform((v) => (v == null || v === '' ? null : v)),
    timeTrackingOpeningBalanceHours: z.number().min(-2000).max(2000).nullable(),
});
export const TimeDecideStatementSchema = z
    .object({
    statementId: z.string().min(1),
    decision: z.enum(['approved', 'rejected']),
    note: z.string().trim().max(1000).optional(),
})
    .superRefine((d, ctx) => {
    if (d.decision === 'rejected' && !d.note) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['note'],
            message: 'A note is required when rejecting',
        });
    }
});
export const TimeSummaryRowsSchema = z.object({ year: z.number().int().min(2020).max(2100), period: timeSummaryPeriodKey });
export const TimeSummaryPreviewSchema = TimeSummaryRowsSchema.extend({ groupName: z.string().trim().min(1).max(120) });
export const TimeSummarySendNowSchema = TimeSummaryRowsSchema.extend({ saveToVault: z.boolean().optional() });
//# sourceMappingURL=timeTracking.js.map